import { LoggingLevelType, LoggingMessage } from "./types";

/**
 * Nízko úrovňové rozhraní pro tzv. "klientské" logování.
 * Dá se trochu chápat jako SPI (Service Provider Interface).
 * Programátor nejspíš nevyužije přímo, ale sáhne spíše po {@link ISectionLogger}.
 *
 * Počítá se s implementacemi:
 * - pro zalogování přes _console.log_ do prohlížeče
 * - pro poslání logu přes síť (např. na _sentry_ či kamkoli jinam)
 * - log jako záznam v databázi
 *
 * @includeToDoc
 * @see ISectionLogger
 */
export interface ILogger {
    /**
     * Zaloguje jeden záznam, reprezentovaný zadanými parametry. (Zaloguje 1:1, neprovádí žádnou substituci. Za substituci je zodpovědný {@link ISectionLogger}.)
     *
     * POZOR na neserializovatelné argumenty
     * -------------------------------------
     * Prohlížeč přes _console.log_ zobrazí kde co.
     * Ale posílat např. reactí eventu (se vším svým zanořením) přes internet či ukládat do databky ... Muselo by se stanovit, co z toho vyzobáme a co nikoliv.
     * Zatím stanoveno není.
     * **Proto raději používej s rozumem - a loguj jen argumenty s jasnou reprezentací a s vědomím, že se mohou posílat přes internat či ukládat do DB.**
     * Např. jednoduše převeditelné do JSON.
     *
     * Neloguje referencí, ale snapshotem
     * ----------------------------------
     * Implementace by měly zajistit, že předané argumenty se zalogují v okamžiku předání jako **"SNAPSHOT"**.
     * Tj. Aby v logu byla informace z doby zalogování. (Nikoli z doby zobrazení logu.)
     * (Týká se typicky implementace logující přes _console.log_.)
     *
     * Práh logování
     * -------------
     * Neřešíme. O toto se postarají vyšší vrstvy. Např. {@link ISectionLogger}.
     * - Pozn. Jde o odfiltrovávání logů na základě jejich závažnosti - tj. {@link LoggingLevelType}.
     * - Záměr je, aby implementace {@link ILogger} zalogovaly vše. Aby už si samy nic nefiltrovaly.
     *
     * @param section Jméno sekce, které se záznam týká (Např. "MainMenu", "InitParam", "ServiceWorker", ...).
     * @param level Závažnost záznamu.
     * @param message Nosná zpráva záznamu. Píšeme v angličtině. Může obsahovat substituční znaky - viz výše.
     * @param args Volitelné argumenty - dvou typů:
     * 1. Pokud `message` je string a obsahuje substituční znaky (např. `%s`), pak tu musí ke každému substitučnímu znaku být odpovídající argument.
     * (Klasika. Např. jako u _console.log_ v prohlížeči.)
     * (Je pak na konkrétní implementaci loggeru, jak k tomu přistoupí. Záměr je, že se tyto argumenty použijí na sestavení výsledné zprávy.)
     * Např. pokud bychom log posílali přes internet, pak se tyto substituční argumenty nebudou posílat, ale pošle se jen výsledná zpráva po substituci.)
     * 2. Argumenty nad rámec substitucí. Součást nebo doplnění nosné informace. Např. zanořený JSON,
     * který by se obtížně inlineoval substitucí do hlavní zprávy.
     */
    log(section: string, level: LoggingLevelType, message: LoggingMessage, ...args: any[]): void;

    /**
     * Zaloguje zadanou výjimku. K výjimce by měl zalogovat jméno třídy, message a stackTrace.
     * @param section Sekce, v rámci které k výjimce došlo.
     * @param level Závažnost záznamu.
     * @param exception Výjimka k zalogování.
     */
    logException(section: string, level: LoggingLevelType, exception: Error): void;

    /**
     * Má stejnou sémantiku jako `console.group`. Tj:
     * - Zaloguje stejně jako {@link log}.
     * - A tento záznam navíc označí jako "rodičovský" tak, že všechny následující volání {@link log} a {@link logException} vytvoří dceřiné záznamy.
     * A to až do provolání {@link groupEnd}.
     *
     * # Zamýšlený usecase
     * ```
     * logger.group("main information");
     * logger.log("detail1");
     * logger.log("detail2");
     * logger.log("detail3");
     * logger.groupEnd();
     * ```
     *
     * # Úskalí
     * Pokud bude používáno dle výše zamýšleného usecase (a pokud budou implementace {@link ILogger} správně naimplementovány),
     * pak se asi není čeho bát.
     *
     * ## Nechtěný log ze `setInterval` či `setTimeout`
     * - Kdybychom porušili usecase výše a logovali do groupy nestandardně, tak by se mohlo stát, že se nám do groupy přimíchají cizí logy.
     *   Např. z funkcí volaných z `setTimeout` či `setInterval`.
     * - Pokud budeme logovat dle usecase výše, pak by toto - snad - hrozit nemělo. (Javascript - zdá se - může volat funkci z "timeru" až poté, co je prázdný callstack.)
     *
     * ## Výjimky
     * - Vyhození výjimky by mohlo způsobit, že se nezavolá {@link groupEnd}, čímž by se vše dál logovalo do groupy.
     * - Takže by bylo záhodno zajistit, aby zalogování nikdy nemohlo způsobit výjimku. (Což by bylo záhodno tak jak tak.)
     *
     * # Pro implementátory:
     * - Implementace {@link ILogger} musí zajistit, aby při správném používání {@link group} nedošlo k pomíchání logů. Viz úskalí výše.
     * - Počítat s tím, že volání {@link group} a {@link groupCollapsed} může být rekurzivní.
     */
    group(section: string, message: LoggingMessage, ...args: any[]): void;

    /**
     * Dělá totéž co {@link group}. Rozdíl je ve výchozím zobrazení.
     * - zatímco {@link group} zobrazí expandovaně - rovnou je vidět i dceřiné záznamy
     * - tak {@link groupCollapsed} zobrazí collapsovaně - dceřiné záznamy vidět nejsou
     */
    groupCollapsed(section: string, message: LoggingMessage, ...args: any[]): void;

    /**
     * Má stejnou sémantiku jako `console.groupEnd`. Tj. Ruší efekt seskupování naposledy volané {@link group} či {@link groupCollapsed}.
     */
    groupEnd(): void;
}

export class ILogger {
    private static _properties: (keyof ILogger)[] = [];
    private static _methods = ["log", "logException", "group", "groupCollapsed", "groupEnd"];

    // zápis kvůli ES5
    public static get [Symbol.hasInstance](): (instance: any) => boolean {
        return (instance: any) => instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) &&
        !this._methods.some(value => typeof instance[value] !== "function");
    }
}
